/**
 * LLM BYOK credentials — browser-local storage service (W6 / OQ-3).
 *
 * Trevor closed OQ-3 (2026-08-06 Slack: "yes browser storage sounds good for
 * now"), so v1 stores the user's provider + model + API key in a single
 * namespaced `localStorage` entry. There is intentionally NO server-side
 * persistence anywhere — the FastAPI route accepts the key per request, uses
 * it in-memory to invoke the provider, and drops it (see
 * `functions/services/deep_dive_service.py` for the belt-and-suspenders
 * scrubbing on the backend). Encrypted server-side storage is a v2
 * conversation if/when UX friction becomes real.
 *
 * Security invariants (spec Decision 7, non-negotiable):
 *   - The API key must appear at runtime in exactly two places: the
 *     `LlmSettingsDialog` input control and the request body of
 *     `runDeepDive`. Grep the diff for `LLM_CREDENTIALS_STORAGE_KEY` — nothing
 *     other than this module should read or write that entry.
 *   - Never log the key. Never put it in a URL. Never put it in error UI copy.
 *   - Never send the key through the `appearance_preferences` autosave chain
 *     (that would round-trip it through the FastAPI backend).
 *
 * Provider/model choices are fetched from the backend-owned model catalog.
 * The small fallback below keeps the settings surface usable during a
 * transient catalog request failure; the backend still re-validates every
 * Deep Dive request and remains the security authority.
 */

// Namespaced key so it never collides with other browser-local state. The
// `:v1` suffix future-proofs a migration if the stored shape ever changes.
export const LLM_CREDENTIALS_STORAGE_KEY = 'alphamizer:llm_byok:v1';

/**
 * Boot fallback for the current server catalog. New model publications are
 * delivered through GET /api/news/deep_dive/models without a frontend code
 * change. The backend remains authoritative if this fallback becomes stale.
 */
export const ALLOWED_MODELS = Object.freeze({
    openai: Object.freeze(['gpt-6-luna', 'gpt-6-astra']),
    anthropic: Object.freeze(['claude-sonnet-5', 'claude-opus-4-8']),
});

export const PROVIDER_LABELS = Object.freeze({
    openai: 'OpenAI',
    anthropic: 'Anthropic',
});

export const MODEL_LABELS = Object.freeze({
    'gpt-6-luna': 'GPT-6 Luna - Quick, lower cost',
    'gpt-6-astra': 'GPT-6 Astra - Deep, highest quality',
    'claude-sonnet-5': 'Claude Sonnet 5 - Balanced',
    'claude-opus-4-8': 'Claude Opus 4.8 - Deep',
});

let activeAllowedModels = ALLOWED_MODELS;
let activeProviderLabels = PROVIDER_LABELS;
let activeModelLabels = MODEL_LABELS;
let activeDefaultProvider = 'openai';
let activeDefaultModel = 'gpt-6-luna';

const LEGACY_MODEL_MIGRATIONS = Object.freeze({
    'openai:gpt-5': 'gpt-6-luna',
});

/** Apply a validated public catalog response from the backend. */
export function applyModelCatalog(catalog) {
    if (!catalog || !Array.isArray(catalog.providers) || catalog.providers.length === 0) {
        throw new Error('Model catalog is empty or malformed.');
    }
    const nextAllowed = {};
    const nextProviderLabels = {};
    const nextModelLabels = {};
    catalog.providers.forEach((providerEntry) => {
        const provider = typeof providerEntry?.id === 'string'
            ? providerEntry.id.trim().toLowerCase()
            : '';
        if (!provider || !Array.isArray(providerEntry.models)) return;
        const models = providerEntry.models
            .map((entry) => (typeof entry?.id === 'string' ? entry.id.trim() : ''))
            .filter(Boolean);
        if (models.length === 0) return;
        nextAllowed[provider] = Object.freeze(models);
        nextProviderLabels[provider] = typeof providerEntry.label === 'string'
            ? providerEntry.label
            : provider;
        providerEntry.models.forEach((entry) => {
            if (typeof entry?.id !== 'string' || !entry.id.trim()) return;
            nextModelLabels[entry.id.trim()] = typeof entry.label === 'string'
                ? entry.label
                : entry.id.trim();
        });
    });
    const providers = Object.keys(nextAllowed);
    if (providers.length === 0) throw new Error('Model catalog has no usable providers.');

    activeAllowedModels = Object.freeze(nextAllowed);
    activeProviderLabels = Object.freeze(nextProviderLabels);
    activeModelLabels = Object.freeze(nextModelLabels);
    const requestedProvider = typeof catalog.default_provider === 'string'
        ? catalog.default_provider.trim().toLowerCase()
        : '';
    activeDefaultProvider = nextAllowed[requestedProvider]
        ? requestedProvider
        : providers[0];
    const requestedModel = typeof catalog.default_model === 'string'
        ? catalog.default_model.trim()
        : '';
    activeDefaultModel = nextAllowed[activeDefaultProvider].includes(requestedModel)
        ? requestedModel
        : nextAllowed[activeDefaultProvider][0];
}

/** Restore deterministic fallback state between tests. */
export function resetModelCatalog() {
    activeAllowedModels = ALLOWED_MODELS;
    activeProviderLabels = PROVIDER_LABELS;
    activeModelLabels = MODEL_LABELS;
    activeDefaultProvider = 'openai';
    activeDefaultModel = 'gpt-6-luna';
}

export function getDefaultProvider() {
    return activeDefaultProvider;
}

export function getDefaultModel(provider = activeDefaultProvider) {
    const models = listModelsForProvider(provider);
    if (provider === activeDefaultProvider && models.includes(activeDefaultModel)) {
        return activeDefaultModel;
    }
    return models[0] || '';
}

export function getProviderLabel(provider) {
    return activeProviderLabels[provider] || provider;
}

export function getModelLabel(model) {
    return activeModelLabels[model] || model;
}

/**
 * Return the list of supported providers (ordered for the UI dropdown).
 */
export function listProviders() {
    return Object.keys(activeAllowedModels);
}

/**
 * Return the list of allowed models for a provider, or `[]` if the provider
 * is unknown. Never throws — the UI uses this to populate the model select.
 */
export function listModelsForProvider(provider) {
    const key = typeof provider === 'string' ? provider.trim().toLowerCase() : '';
    if (!key || !Object.prototype.hasOwnProperty.call(activeAllowedModels, key)) {
        return [];
    }
    return [...activeAllowedModels[key]];
}

/**
 * `true` if `{provider, model}` are both non-empty and both on the frontend
 * allowlist. Backend re-validates, so this is UX-only.
 */
export function isValidProviderModelPair(provider, model) {
    const providerKey = typeof provider === 'string' ? provider.trim().toLowerCase() : '';
    if (!providerKey || !Object.prototype.hasOwnProperty.call(activeAllowedModels, providerKey)) {
        return false;
    }
    const modelKey = typeof model === 'string' ? model.trim() : '';
    if (!modelKey) return false;
    return activeAllowedModels[providerKey].includes(modelKey);
}

function safeGetStorage() {
    // JSDOM + Node envs may not have `localStorage`; guard so the service is
    // safe to import from SSR / test contexts even before mock is set up.
    if (typeof window === 'undefined') return null;
    try {
        return window.localStorage || null;
    } catch (_err) {
        return null;
    }
}

/**
 * Read the stored credentials. Returns `null` when nothing is stored, the
 * entry is malformed, or any field is missing / non-string. Never throws.
 *
 * The consumer treats `null` as "no key on file" and routes the user through
 * `LlmSettingsDialog` before making a deep-dive request.
 */
export function loadLlmCredentials() {
    const storage = safeGetStorage();
    if (!storage) return null;
    let raw;
    try {
        raw = storage.getItem(LLM_CREDENTIALS_STORAGE_KEY);
    } catch (_err) {
        return null;
    }
    if (raw === null || raw === undefined || raw === '') return null;
    let parsed;
    try {
        parsed = JSON.parse(raw);
    } catch (_err) {
        return null;
    }
    if (!parsed || typeof parsed !== 'object') return null;
    const provider = typeof parsed.provider === 'string' ? parsed.provider.trim() : '';
    const model = typeof parsed.model === 'string' ? parsed.model.trim() : '';
    const apiKey = typeof parsed.api_key === 'string' ? parsed.api_key : '';
    if (!provider || !model || !apiKey) return null;
    const normalizedProvider = provider.toLowerCase();
    const migratedModel = LEGACY_MODEL_MIGRATIONS[`${normalizedProvider}:${model}`];
    return {
        provider: normalizedProvider,
        model: migratedModel && isValidProviderModelPair(normalizedProvider, migratedModel)
            ? migratedModel
            : model,
        api_key: apiKey,
    };
}

/**
 * Persist the credentials. Validates provider/model against the allowlist
 * and rejects an empty key with a thrown Error so the dialog can surface a
 * validation message. Never logs.
 */
export function saveLlmCredentials({ provider, model, api_key: apiKey } = {}) {
    const storage = safeGetStorage();
    if (!storage) {
        throw new Error('Browser storage is not available.');
    }
    if (!isValidProviderModelPair(provider, model)) {
        throw new Error('Provider or model is not on the allowlist.');
    }
    if (typeof apiKey !== 'string' || apiKey.trim() === '') {
        throw new Error('API key must not be empty.');
    }
    const payload = {
        provider: String(provider).trim().toLowerCase(),
        model: String(model).trim(),
        api_key: apiKey,
    };
    // NOTE: `JSON.stringify` is the only place the raw key touches a string
    // outside `runDeepDive`'s request body. It does not go through any logger
    // or reporter.
    storage.setItem(LLM_CREDENTIALS_STORAGE_KEY, JSON.stringify(payload));
}

/**
 * Remove the stored credentials. Safe to call when nothing is stored (no-op).
 */
export function clearLlmCredentials() {
    const storage = safeGetStorage();
    if (!storage) return;
    try {
        storage.removeItem(LLM_CREDENTIALS_STORAGE_KEY);
    } catch (_err) {
        // Ignore — soft-fail matches the "browser storage may be unavailable"
        // contract used across the rest of this service.
    }
}

/**
 * Cheap "should I skip the settings prompt?" check. Returns `true` iff
 * `loadLlmCredentials()` would return a well-formed entry.
 */
export function hasStoredCredentials() {
    return loadLlmCredentials() !== null;
}
